// PremiumLock — wraps a UI element behind the premium-tier gate.
//
//   <PremiumLock category="Hydrogen" variant="lock">
//     <button onClick={selectHydrogen}>Hydrogen</button>
//   </PremiumLock>
//
//   <PremiumLock category="Hazards" variant="blur">
//     <HazardsFilterRow />
//   </PremiumLock>
//
// For premium users: renders {children} unchanged (no wrapper).
// For non-premium: blocks pointer events on the children, overlays a
// 🔒 (lock variant) or blur + CTA (blur variant). Lock click toggles a
// small popover with the upgrade link; blur click opens the link
// directly.
//
// This is UX only. Backend MUST independently enforce the underlying
// restriction — anyone can DevTools-bypass these styles.

import React, { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useIsPremium } from '../../contexts/PremiumContext';
import { calendlyWithPrefill } from '../../utils/calendly';
import './PremiumLock.css';

export default function PremiumLock({
  category,
  variant = 'lock',
  // Optional: caller can short-circuit the gate (e.g. when conditional
  // on something other than premium status — used by the project cap).
  forceLocked,
  className = '',
  children,
}) {
  const { isPremium } = useIsPremium();
  // 2026-06-07: every premium-gated CTA opens Calendly with the
  // Auth0 email pre-filled. Pull the email here so both the popover
  // button click and the inline upgrade link can prefill.
  const { user: auth0User } = useAuth0();
  const UPGRADE_URL = calendlyWithPrefill(auth0User?.email, auth0User?.name);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Close popover on outside click. Mounted only when open so we don't
  // pay for an extra global listener per PremiumLock instance.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const locked = forceLocked != null ? forceLocked : !isPremium;
  if (!locked) return children;

  if (variant === 'blur') {
    return (
      <div
        className={`premium-blur ${className}`.trim()}
        onClick={(e) => {
          e.preventDefault();
          window.open(UPGRADE_URL, '_blank', 'noopener,noreferrer');
        }}
        title={`${category || 'Premium feature'} — Book a call`}
      >
        <div className="premium-blur-content" aria-hidden="true">{children}</div>
        <div className="premium-blur-overlay">
          {category && <div className="premium-blur-category">{category}</div>}
          <span className="premium-blur-cta">Book a call →</span>
        </div>
      </div>
    );
  }

  // variant === 'lock'
  return (
    <div
      ref={wrapRef}
      className={`premium-lock ${className}`.trim()}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setOpen((v) => !v);
      }}
    >
      <div className="premium-lock-content">{children}</div>
      <span className="premium-lock-icon" aria-hidden="true">🔒</span>
      {open && (
        <div className="premium-popover" role="tooltip">
          {category ? `${category} — ` : ''}Available in Premium
          <a
            className="premium-popover-cta"
            href={UPGRADE_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            Book a call →
          </a>
        </div>
      )}
    </div>
  );
}

// Whole-panel replacement card. Use when the entire pane is gated
// (DD panel, locked tabs in ProjectsPage). Renders a centered card
// with title, body, and a single upgrade CTA. Doesn't read isPremium —
// callers decide when to swap in the card vs the real content.
export function LockedCard({ title, body, ctaLabel = 'Book a call →' }) {
  // Pull Auth0 user here too — UPGRADE_URL was moved inside
  // PremiumLock's body in the 2026-06-07 Calendly migration so it
  // could read the email; this component shares that pattern but
  // is a separate export and was crashing with "UPGRADE_URL is not
  // defined" on render. That ReferenceError took the React tree
  // down → black screen on Pipeline / Timeline tabs for free users.
  const { user: auth0User } = useAuth0();
  const url = calendlyWithPrefill(auth0User?.email, auth0User?.name);
  return (
    <div className="premium-locked-card">
      <div className="premium-locked-card-icon" aria-hidden="true">🔒</div>
      {title && <div className="premium-locked-card-title">{title}</div>}
      {body && <div className="premium-locked-card-body">{body}</div>}
      <a
        className="premium-locked-card-cta"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
      >
        {ctaLabel}
      </a>
    </div>
  );
}
